import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import GxpReportHeader from './components/GxpReportHeader';
import GxpReportTitleSection from './components/GxpReportTitleSection';
import GxpReportSidebar from './components/GxpReportSidebar';
import StatusBanner from './components/StatusBanner';
import RequirementsCard from './components/RequirementsCard';
import TemperatureProfileChart from './components/TemperatureProfileChart';
import type { TemperatureSeries } from './components/temperatureProfile';
import ExcursionEventsTable from './components/ExcursionEventsTable';
import SensorsTable from './components/SensorsTable';
import { GxpReportData } from './components/types';
import { useToken } from '@/common/helper';
import useUnauthorize from '@/hooks/useUnauthorize';
import { useSpin } from '@/components/SpinContext';
import { GetReportCharts, GetReportDetails } from '@/apis/gxpReport';
import { trackEvent } from '@/hooks';
import { BeaconEventType } from '@/common/enum';

const GxPReportGenrationPage = () => {
  const { id } = useParams<{ id: string }>();
  const { token } = useToken();
  const { unauthorize } = useUnauthorize();
  const { showSpin, hideSpin } = useSpin();

  const [report, setReport] = useState<GxpReportData>({});
  const [reportChart, setReportChart] = useState<TemperatureSeries>({});

  const [activeSensorId, setActiveSensorId] = useState('');
  const fetchReportDetails = useCallback(() => {
    if (!token) return;
    showSpin();
    const queryParam = activeSensorId
      ? `${id}/?sensor_tracking_id=${activeSensorId}`
      : `${id}/`;

    GetReportDetails(queryParam, token)
      .then((res) => {
        const data = res?.data?.data?.data;

        if (data) {
          setReport(data);
        }
      })
      .catch((error) => {
        unauthorize(error);
      })
      .finally(() => {
        hideSpin();
      });
  }, [id, activeSensorId, token, showSpin, hideSpin, unauthorize]);

  const fetchReportChart = useCallback(() => {
    if (!token) return;
    showSpin();
    const queryParam = activeSensorId
      ? `${id}/temperature-series/?sensor_tracking_id=${activeSensorId}`
      : `${id}/temperature-series/`;
    GetReportCharts(queryParam, token)
      .then((res) => {
        const data = res?.data?.data?.data;
        if (data) {
          setReportChart(data);
        }
      })
      .catch((error) => {
        unauthorize(error);
      })
      .finally(() => {
        hideSpin();
      });
  }, [id, activeSensorId, token, showSpin, hideSpin, unauthorize]);

  useEffect(() => {
    fetchReportDetails();
    fetchReportChart();
  }, [fetchReportDetails, fetchReportChart]);

  // Track viewing this GXP report once its detail has loaded (shipment_id
  // only arrives with the fetch response, not synchronously with the route
  // param), keyed on report.shipment_id so a re-fetch (e.g. sensor switch)
  // doesn't re-log the same report/shipment pair.
  useEffect(() => {
    if (!id || !report?.shipment_id) return;
    trackEvent(BeaconEventType.ReportViewed, {
      reference_id: id,
      shipment_id: report.shipment_id,
    });
  }, [id, report?.shipment_id]);

  const handleRefreshPageData = () => {
    fetchReportDetails();
    fetchReportChart();
  };
  //   reviewer data
  const reviewerData = {
    published_at: report?.published_at,
    published_by_name: report?.published_by_name,
    reviewer_note: report?.reviewer_note,
    review_action: report?.review_action,
    status: report?.status,
    status_label: report?.status_label,
  };

  return (
    <>
      <GxpReportHeader
        reportID={id}
        refID={report?.shipment_reference ?? '--'}
        canPublish={report?.can_review === true}
        exportNoticeText={report?.exportNoticeText ?? ''}
        handleRefreshPageData={handleRefreshPageData}
      />

      <div className='gxp-report-page'>
        <GxpReportTitleSection
          referenceID={report.shipment_reference ?? '--'}
          originAddress={report.shipment_route_label ?? ''}
          sensors={report.sensors ?? []}
          activeSensorId={activeSensorId}
          onSensorChange={setActiveSensorId}
        />

        <div className='gxp-report-grid'>
          <div className='gxp-main-section'>
            {report?.banner && <StatusBanner banner={report?.banner} />}
            {report.requirements_summary && (
              <RequirementsCard requirements={report.requirements_summary} />
            )}

            {reportChart && (
              <div className='gxp-card'>
                <div className='gxp-card__title'>Temperature profile</div>
                <TemperatureProfileChart
                  chartApiData={reportChart}
                  excursionEvents={report.excursion_events ?? []}
                />
              </div>
            )}

            <ExcursionEventsTable events={report?.excursion_events ?? []} />

            {report.sensor_results && (
              <SensorsTable sensors={report.sensor_results} />
            )}
          </div>

          <GxpReportSidebar
            isAutoPublish={report?.is_auto_publish}
            reviewers={reviewerData ?? {}}
            limitsApplied={
              report?.limits_applied ?? {
                temperature_range_label: 'Temperature range',
                temperatureRangeValue: '',
                mktLabel: 'MKT limit',
                mktValue: '',
                activationEnergyLabel: 'Activation energy',
                activationEnergyValue: '',
              }
            }
            shipmentDetails={
              report?.shipment_detail ?? {
                origin: '--',
                destination: '--',
                pickupLabel: 'Pickup (POP)',
                picked_up_at: '--',
                deliveryLabel: 'Delivery (POD)',
                delivered_at: '--',
              }
            }
            products={report?.products ?? []}
            references={report?.references ?? []}
          />
        </div>
      </div>
    </>
  );
};

export default GxPReportGenrationPage;
